import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../configuration/setup/store';
import { ChargingStation } from '../../components/infrastructure/chargingStation/chargingStation.types';
import { Group, GroupWithoutStationsList } from '../../features/infrastructure/group/group.types';
import { Grid } from '../../features/infrastructure/grid/grid.types';

export interface InfrastructureState {
    chargers: ChargingStation[];
    groups: GroupWithoutStationsList[];
    grids: Grid[];
}

const initialState: InfrastructureState = {
    chargers: [],
    groups: [],
    grids: [],
};

const infrastructureSlice = createSlice({
    name: 'infrastructure',
    initialState,
    reducers: {
        storeCharger: (state, action: PayloadAction<ChargingStation>) => {
            const chargerIndex = state.chargers.findIndex(
                (charger) => charger.chargingStationId === action.payload.chargingStationId
            );

            if (chargerIndex !== -1) {
                state.chargers[chargerIndex] = action.payload;
            }
        },
        storeChargers: (state, action: PayloadAction<ChargingStation[]>) => {
            const chargers = state.chargers.map((charger) => {
                const isUpdated = action.payload.find((item) => item.chargingStationId === charger.chargingStationId);
                return isUpdated ? isUpdated : charger;
            });
            state.chargers = chargers;
        },
        storeAllChargers: (state, action: PayloadAction<ChargingStation[]>) => {
            state.chargers = action.payload;
        },
        updateGroup: (state, action: PayloadAction<Group>) => {
            const { chargingStationsList, ...groupWithoutStationsList } = action.payload;

            const groupIndex = state.groups.findIndex((group) => group.id === action.payload.id);

            if (groupIndex !== -1) {
                state.groups[groupIndex] = groupWithoutStationsList;
            } else {
                state.groups.push(groupWithoutStationsList);
            }
        },
        storeAllGroups: (state, action: PayloadAction<Group[]>) => {
            state.groups = action.payload.map(({ chargingStationsList, ...rest }) => rest);
        },
        deleteGroup: (state, action: PayloadAction<Group['id']>) => {
            const allGroups = state.groups.filter((group) => group.id !== action.payload);
            state.groups = allGroups;
        },
        storeAllGrids: (state, action: PayloadAction<Grid[]>) => {
            state.grids = action.payload;
        },
    },
});

export const {
    storeAllChargers,
    storeCharger,
    storeAllGroups,
    updateGroup,
    storeChargers,
    deleteGroup,
    storeAllGrids,
} = infrastructureSlice.actions;

export const getChargers = (state: RootState) => state.infrastructure.chargers ?? 'NO_CHARGERS_AVAILABLE';
export const getGroups = (state: RootState) => state.infrastructure.groups ?? 'NO_GROUPS_AVAILABLE';
export const getGrids = (state: RootState) => state.infrastructure.grids ?? 'NO_GRIDS_AVAILABLE';

export default infrastructureSlice.reducer;
